import axios from 'axios'
import React from 'react'

import { CostsBreakdown } from '@components/common/CostsBreakdown'
import type { BannerProps } from '@fourthwall/components'
import { Banner, Loader, ModalBox, Toasts, useModalContext } from '@fourthwall/components'
import { errorMessageSelector } from '@fourthwall/utils/dist/selectors'
import { routing } from '@utils/routing'

import type { Order } from '../../Order.types'
import { useCostsBreakdownQuery } from './queries/useCostsBreakdownQuery'

type OrderCostsBreakdownModalProps = {
  orderId: string
  order: Order
}

export const OrderCostsBreakdownModal = ({ orderId, order }: OrderCostsBreakdownModalProps) => {
  const { close } = useModalContext()

  const { orderCostsBreakdownQuery } = useCostsBreakdownQuery([orderId], {
    onError: (error: unknown) => {
      if (axios.isAxiosError(error)) {
        const errorMessage = errorMessageSelector(
          error.response?.data,
          'Failed to fetch costs breakdown. Please try again.',
        )
        Toasts.notify(errorMessage, { type: 'error' })
      }

      close()
    },
  })

  const renderBanners = () => {
    const banners: BannerProps[] = []

    if (orderCostsBreakdownQuery?.data && order?.salesChannel.type === 'TikTok') {
      banners.push({
        children: (
          <>
            Below you see only Fourthwall manufacturing and shipping costs for this order. To see
            the profit you made visit{' '}
            <a
              href={`${routing.external.tiktok.orderDetails}?order_no=${order?.salesChannel?.id}&shop_region=US`}
              target="_blank"
              rel="noopener noreferrer"
            >
              TikTok Seller Center
            </a>
            .
          </>
        ),
        appearance: 'warning',
      })
    }

    return banners.map((banner, index) => (
      <div style={{ marginBottom: 20 }}>
        <Banner key={index} {...banner} />
      </div>
    ))
  }

  const renderContent = () => {
    if (!orderCostsBreakdownQuery.data) {
      return (
        <div
          style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Loader size="large" />
        </div>
      )
    }

    return <CostsBreakdown costsBreakdown={orderCostsBreakdownQuery.data} />
  }

  return (
    <ModalBox title="Costs breakdown" onClose={close}>
      {renderBanners()}
      {renderContent()}
    </ModalBox>
  )
}
